const HEIC_DECODE_TIMEOUT_MS = 60_000;
const HEIF_MIME_TYPES = {
  heic: 'image/heic',
  heix: 'image/heic',
  heim: 'image/heic',
  heis: 'image/heic',
  hevc: 'image/heic-sequence',
  hevx: 'image/heic-sequence',
  hevm: 'image/heic-sequence',
  hevs: 'image/heic-sequence',
  mif1: 'image/heif',
  msf1: 'image/heif-sequence',
};

export async function resizeImageToWidth(
  inputBytes,
  targetWidth,
  allowUpscaling,
  mimeType = 'image/jpeg'
) {
  const img = await loadImage(inputBytes, mimeType);

  try {
    if (img.width <= 0 || img.height <= 0) {
      throw new Error('Image dimensions must be greater than zero');
    }

    // Calculate scaled dimensions
    const scaledWidth = allowUpscaling
      ? targetWidth
      : Math.min(img.width, targetWidth);
    const aspect = img.height / img.width;
    const scaledHeight = Math.max(1, Math.round(scaledWidth * aspect));

    // Create canvas and draw
    const canvas = document.createElement('canvas');
    canvas.width = scaledWidth;
    canvas.height = scaledHeight;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, scaledWidth, scaledHeight);

    // Extract as data URL (always convert to JPEG for consistency)
    const dataUrl = canvas.toDataURL('image/jpeg', 1.0);
    const base64 = dataUrl.split(',')[1];

    // Convert Base64 -> Uint8Array
    const binary = atob(base64);
    const len = binary.length;
    const outputBytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      outputBytes[i] = binary.charCodeAt(i);
    }

    return outputBytes;
  } finally {
    releaseImage(img);
  }
}

export async function cropImageToSize(
  inputBytes,
  targetWidth,
  targetHeight,
  mimeType = 'image/jpeg'
) {
  const img = await loadImage(inputBytes, mimeType);

  try {
    if (img.width <= 0 || img.height <= 0) {
      throw new Error('Image dimensions must be greater than zero');
    }
    if (targetWidth <= 0 || targetHeight <= 0) {
      throw new Error('Target dimensions must be greater than zero');
    }
    const targetAspect = targetWidth / targetHeight;
    const sourceAspect = img.width / img.height;
    const cropWidth = sourceAspect > targetAspect
      ? Math.max(1, Math.floor(img.height * targetAspect))
      : img.width;
    const cropHeight = sourceAspect > targetAspect
      ? img.height
      : Math.max(1, Math.floor(img.width / targetAspect));
    const sourceX = Math.floor((img.width - cropWidth) / 2);
    const sourceY = Math.floor((img.height - cropHeight) / 2);

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(
      img,
      sourceX,
      sourceY,
      cropWidth,
      cropHeight,
      0,
      0,
      targetWidth,
      targetHeight
    );

    const dataUrl = canvas.toDataURL(
      detectImageFormat(inputBytes) === 'png' ? 'image/png' : 'image/jpeg',
      1.0
    );
    const binary = atob(dataUrl.split(',')[1]);
    const outputBytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      outputBytes[i] = binary.charCodeAt(i);
    }
    return outputBytes;
  } finally {
    releaseImage(img);
  }
}

async function loadImage(inputBytes, mimeType) {
  const detectedFormat = detectImageFormat(inputBytes);
  const blob = new Blob(
    [inputBytes],
    { type: mimeTypeForDetectedFormat(detectedFormat) || mimeType }
  );

  try {
    return await loadBrowserImage(blob);
  } catch (nativeDecodeError) {
    if (!detectedFormat.startsWith('heif (')) {
      throw imageDecodeError(inputBytes, detectedFormat, nativeDecodeError);
    }

    try {
      return await loadHeifImage(inputBytes);
    } catch (heicDecodeError) {
      throw imageDecodeError(inputBytes, detectedFormat, heicDecodeError, true);
    }
  }
}

function loadHeifImage(inputBytes) {
  const workerBytes = new Uint8Array(inputBytes.length);
  workerBytes.set(inputBytes);

  return new Promise((resolve, reject) => {
    let worker;
    let settled = false;
    try {
      // The worker and decoder bundle are fetched only after native HEIC decoding fails.
      worker = new Worker(new URL('./heicDecoderWorker.js', import.meta.url), { type: 'module' });
    } catch (error) {
      reject(error);
      return;
    }

    const timeout = setTimeout(() => {
      finish(() => reject(new Error('HEIC decode worker timed out.')));
    }, HEIC_DECODE_TIMEOUT_MS);
    const finish = (complete) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      worker.terminate();
      complete();
    };

    worker.onmessage = (event) => {
      if (event.data?.bitmap) {
        finish(() => resolve(event.data.bitmap));
      } else {
        finish(() => reject(new Error(event.data?.error || 'HEIC decode worker failed.')));
      }
    };
    worker.onerror = (event) => {
      finish(() => reject(new Error(event.message || 'HEIC decode worker failed.')));
    };
    worker.onmessageerror = () => {
      finish(() => reject(new Error('HEIC decode worker returned an invalid response.')));
    };

    try {
      worker.postMessage(workerBytes.buffer, [workerBytes.buffer]);
    } catch (error) {
      finish(() => reject(error));
    }
  });
}

async function loadBrowserImage(blob) {
  const url = URL.createObjectURL(blob);
  const img = new Image();

  try {
    await new Promise((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Native browser image decode failed.'));
      img.src = url;
    });
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function releaseImage(image) {
  if (typeof image.close === 'function') {
    image.close();
  }
}

function imageDecodeError(inputBytes, detectedFormat, cause, heicFallbackFailed = false) {
  const failure = heicFallbackFailed
    ? 'The selected HEIC image could not be decoded'
    : 'This browser could not decode the selected image';
  const recovery = heicFallbackFailed
    ? 'Please try another HEIC photo or choose a JPEG, PNG, or WebP image.'
    : 'Please choose a JPEG, PNG, or WebP photo.';
  return new Error(
    `${failure} (${inputBytes.length} bytes). Detected format: ${detectedFormat}. ${recovery}`,
    { cause }
  );
}

function mimeTypeForDetectedFormat(detectedFormat) {
  if (detectedFormat === 'jpeg') return 'image/jpeg';
  if (detectedFormat === 'png') return 'image/png';
  if (detectedFormat === 'webp') return 'image/webp';
  if (detectedFormat === 'gif') return 'image/gif';
  if (detectedFormat.startsWith('avif (')) return 'image/avif';
  if (detectedFormat.startsWith('heif (')) {
    const brand = detectedFormat.slice(6, -1);
    return HEIF_MIME_TYPES[brand] || null;
  }
  return null;
}

function detectImageFormat(bytes) {
  if (!bytes || bytes.length < 4) return 'unknown';
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'jpeg';
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47 &&
    bytes[4] === 0x0d && bytes[5] === 0x0a && bytes[6] === 0x1a && bytes[7] === 0x0a
  ) return 'png';
  if (
    bytes.length >= 12 &&
    asciiAt(bytes, 0, 4) === 'RIFF' && asciiAt(bytes, 8, 4) === 'WEBP'
  ) return 'webp';
  if (asciiAt(bytes, 0, 3) === 'GIF') return 'gif';
  if (bytes.length >= 12 && asciiAt(bytes, 4, 4) === 'ftyp') {
    const brand = asciiAt(bytes, 8, 4).toLowerCase();
    if (brand === 'avif' || brand === 'avis') return `avif (${brand})`;
    if (Object.hasOwn(HEIF_MIME_TYPES, brand)) return `heif (${brand})`;
    return `iso-bmff (${brand})`;
  }
  return 'unknown';
}

function asciiAt(bytes, offset, length) {
  if (bytes.length < offset + length) return '';
  let value = '';
  for (let i = offset; i < offset + length; i++) {
    value += String.fromCharCode(bytes[i]);
  }
  return value;
}
